import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Search, Trash2, X, Loader, Camera, Image as ImageIcon, Calendar, CheckCircle, Images } from 'lucide-react';
import api from '../../utils/api';
import { resizeImage } from '../../utils/imageUtils';

const inputCls  = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500";
const selectCls = `${inputCls} bg-white`;

const BLANK = { customer_name:'', customer_phone:'', vehicle:'', rego:'', condition:'', paid:'', date:'', notes:'' };

export default function CarPurchases() {
  const [purchases, setPurchases]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [showAdd, setShowAdd]         = useState(false);
  const [showBooking, setShowBooking] = useState(null); // purchase to create booking from
  const [viewing, setViewing]         = useState(null); // purchase to view detail
  const [form, setForm]               = useState(BLANK);
  const [photos, setPhotos]           = useState([]);   // photos for new purchase
  const [uploading, setUploading]     = useState(false);
  const [toast, setToast]             = useState('');
  const [saving, setSaving]           = useState(false);
  const [bookingForm, setBookingForm] = useState({ scheduled_date:'', scheduled_time:'09:00', notes:'', charge:'' });
  const cameraRef  = useRef(null);
  const galleryRef = useRef(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/car-purchases${search ? `?search=${search}` : ''}`);
      setPurchases(r.data);
    } catch(e) {} finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const sf = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const sbf = k => e => setBookingForm(f => ({ ...f, [k]: e.target.value }));

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const newPhotos = await Promise.all(files.map(f => resizeImage(f)));
      setPhotos(prev => [...prev, ...newPhotos]);
    } catch { alert('Photo upload failed — try again.'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const add = async () => {
    if (!form.date || !form.customer_name || !form.vehicle || !form.condition || !form.paid) return;
    setSaving(true);
    try {
      await api.post('/api/car-purchases', {
        ...form,
        paid:   Number(form.paid),
        photos: photos,
      });
      setShowAdd(false);
      setForm(BLANK);
      setPhotos([]);
      showToast('✅ Purchase recorded!');
      load();
    } catch { alert('Failed to record purchase.'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this purchase record?')) return;
    await api.delete(`/api/car-purchases/${id}`);
    showToast('Deleted');
    if (viewing?.id === id) setViewing(null);
    load();
  };

  const createBooking = async () => {
    if (!showBooking || !bookingForm.scheduled_date) return;
    setSaving(true);
    try {
      const parts = showBooking.vehicle?.split(' ') || [];
      await api.post('/api/bookings', {
        customer_name:    showBooking.customer_name,
        customer_phone:   showBooking.customer_phone || '',
        service_type:     'Cash for Cars',
        pickup_address:   '',
        pickup_suburb:    '',
        vehicle_make:     parts[1] || '',
        vehicle_model:    parts.slice(2).join(' ') || '',
        vehicle_year:     parts[0] || '',
        vehicle_rego:     showBooking.rego || '',
        vehicle_condition: showBooking.condition || '',
        scheduled_date:   bookingForm.scheduled_date,
        scheduled_time:   bookingForm.scheduled_time,
        charge:           Number(bookingForm.charge) || Number(showBooking.paid) || 0,
        notes:            bookingForm.notes || `Car purchase — ${showBooking.vehicle}`,
        booking_type:     'admin',
      });
      showToast('✅ Booking created!');
      setShowBooking(null);
      setBookingForm({ scheduled_date:'', scheduled_time:'09:00', notes:'', charge:'' });
    } catch { alert('Failed to create booking.'); }
    finally { setSaving(false); }
  };

  // Copy a photo from a purchase to the gallery
  const copyToGallery = async (photoUrl, vehicle) => {
    try {
      await api.post('/api/gallery', {
        url:      photoUrl,
        title:    vehicle || 'Car Purchase',
        category: 'fleet',
        published: false, // admin can publish later
      });
      showToast('✅ Photo copied to Gallery (unpublished — go to Gallery to publish)');
    } catch { alert('Failed to copy to gallery.'); }
  };

  const total = purchases.reduce((s, c) => s + (c.paid || 0), 0);

  const conditionColor = (c) => {
    const map = { Excellent:'bg-green-100 text-green-700', Good:'bg-blue-100 text-blue-700', Fair:'bg-yellow-100 text-yellow-700', Poor:'bg-orange-100 text-orange-700', Scrap:'bg-gray-100 text-gray-600' };
    return map[c] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Hidden file inputs */}
      <input ref={cameraRef}  type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handleFile} />
      <input ref={galleryRef} type="file" accept="image/*"                       multiple className="hidden" onChange={handleFile} />

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-apex-blue-700 text-white px-5 py-3 rounded-xl font-semibold text-sm shadow-xl z-50 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />{toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-black text-apex-blue-700">Car Purchases</h1>
          <p className="text-sm text-gray-400 mt-0.5">{purchases.length} vehicles · <span className="font-bold text-red-500">${total.toLocaleString()} paid out</span></p>
        </div>
        <button onClick={() => { setForm(BLANK); setPhotos([]); setShowAdd(true); }}
          className="flex items-center gap-2 bg-apex-yellow-500 text-apex-blue-700 px-4 py-2.5 rounded-xl text-sm font-black hover:bg-apex-yellow-400 shadow-md">
          <Plus className="w-4 h-4" /> Record Purchase
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          ['Total Purchased',  purchases.length,                                                     '#3b82f6', 'vehicles'],
          ['Total Paid Out',   `$${total.toLocaleString()}`,                                         '#ef4444', 'in stock spend'],
          ['This Month',       purchases.filter(c => c.date?.slice(0,7) === new Date().toISOString().slice(0,7)).length, '#7c3aed', 'purchased'],
          ['Good–Excellent',   purchases.filter(c => ['Excellent','Good'].includes(c.condition)).length, '#059669', 'quality cars'],
        ].map(([l,v,color,sub]) => (
          <div key={l} className="bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md transition-all">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{l}</p>
            <p className="text-2xl font-black" style={{ color }}>{v}</p>
            <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 mb-4 max-w-md">
        <Search className="w-4 h-4 text-gray-300 shrink-0" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Customer, vehicle, rego..." className="outline-none text-sm flex-1" />
        {search && <button onClick={() => setSearch('')} className="text-gray-300 text-xs hover:text-gray-500">✕</button>}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader className="w-8 h-8 text-apex-blue-500 animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Date','Customer','Vehicle','Rego','Condition','Paid','Photos','Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {purchases.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-16 text-gray-400">
                    <p className="font-medium">No car purchases recorded yet</p>
                    <p className="text-xs mt-1">Click "Record Purchase" to add your first</p>
                  </td></tr>
                )}
                {purchases.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setViewing(c)}>
                    <td className="px-4 py-3.5 text-sm font-medium text-gray-800 whitespace-nowrap">{c.date}</td>
                    <td className="px-4 py-3.5">
                      <p className="font-bold text-gray-900">{c.customer_name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{c.customer_phone}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="font-medium text-gray-800">{c.vehicle}</p>
                      {c.notes && <p className="text-xs text-gray-400 truncate max-w-[140px]">{c.notes}</p>}
                    </td>
                    <td className="px-4 py-3.5 font-mono text-xs text-gray-500">{c.rego || '—'}</td>
                    <td className="px-4 py-3.5">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${conditionColor(c.condition)}`}>{c.condition}</span>
                    </td>
                    <td className="px-4 py-3.5 font-black text-apex-blue-700 text-base whitespace-nowrap">${(c.paid||0).toLocaleString()}</td>
                    <td className="px-4 py-3.5">
                      {(c.photos?.length > 0)
                        ? <span className="text-xs font-bold text-apex-blue-600 bg-apex-blue-50 px-2 py-0.5 rounded-full">{c.photos.length} 📷</span>
                        : <span className="text-xs text-gray-300">None</span>
                      }
                    </td>
                    <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1.5">
                        <button onClick={() => { setBookingForm({ scheduled_date:'', scheduled_time:'09:00', notes:'', charge: String(c.paid||'') }); setShowBooking(c); }}
                          title="Create Booking" className="p-2 rounded-xl border border-blue-200 hover:bg-blue-50 text-apex-blue-600">
                          <Calendar className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => del(c.id)} title="Delete"
                          className="p-2 rounded-xl border border-red-200 hover:bg-red-50 text-red-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {purchases.length > 0 && (
                <tfoot>
                  <tr className="bg-apex-blue-700">
                    <td colSpan={5} className="px-4 py-3 font-bold text-white">Total Spend</td>
                    <td className="px-4 py-3 font-black text-apex-yellow-500 text-lg">${total.toLocaleString()}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* ── View Detail Modal ── */}
      {viewing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => { if(e.target===e.currentTarget) setViewing(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-2xl">
            <div className="bg-apex-blue-700 rounded-t-2xl px-6 py-5 shrink-0">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-white/60 text-xs font-bold uppercase tracking-wider">Car Purchase</p>
                  <p className="text-white font-black text-lg mt-0.5">{viewing.vehicle}</p>
                  <p className="text-white/50 text-xs mt-0.5">{viewing.date} · {viewing.customer_name}</p>
                </div>
                <button onClick={() => setViewing(null)} className="bg-white/15 text-white p-2 rounded-lg"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1 p-5 space-y-4">
              {/* Details */}
              <div className="grid grid-cols-2 gap-3">
                {[['Customer', viewing.customer_name], ['Phone', viewing.customer_phone||'—'],
                  ['Rego', viewing.rego||'—'], ['Condition', viewing.condition],
                  ['Amount Paid', `$${(viewing.paid||0).toLocaleString()}`], ['Date', viewing.date],
                ].map(([l,v]) => (
                  <div key={l} className="bg-gray-50 rounded-xl p-3">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-0.5">{l}</p>
                    <p className="font-bold text-gray-800 text-sm">{v}</p>
                  </div>
                ))}
              </div>
              {viewing.notes && <p className="text-sm text-gray-600 bg-amber-50 border border-amber-100 rounded-xl p-3">📝 {viewing.notes}</p>}

              {/* Photos */}
              {(viewing.photos?.length > 0) ? (
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Photos ({viewing.photos.length})</p>
                  <div className="grid grid-cols-2 gap-2">
                    {viewing.photos.map((src, i) => (
                      <div key={i} className="relative rounded-xl overflow-hidden bg-gray-100 group">
                        <img src={src} alt="" className="w-full aspect-[4/3] object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button onClick={() => copyToGallery(src, viewing.vehicle)}
                            className="bg-white text-apex-blue-700 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 hover:bg-apex-yellow-400">
                            <Images className="w-3.5 h-3.5" /> Copy to Gallery
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">Hover photo → "Copy to Gallery" → go to Gallery admin to publish to website</p>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-300 border-2 border-dashed border-gray-200 rounded-xl">
                  <ImageIcon className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">No photos for this purchase</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2 border-t border-gray-100">
                <button onClick={() => { setBookingForm({ scheduled_date:'', scheduled_time:'09:00', notes:'', charge: String(viewing.paid||'') }); setShowBooking(viewing); setViewing(null); }}
                  className="flex-1 bg-apex-blue-700 text-white py-3 rounded-xl font-bold hover:bg-apex-blue-600 flex items-center justify-center gap-2">
                  <Calendar className="w-4 h-4" /> Create Booking
                </button>
                <button onClick={() => del(viewing.id)}
                  className="px-4 py-3 border-2 border-red-200 text-red-500 rounded-xl font-bold hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Booking Modal ── */}
      {showBooking && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={e => { if(e.target===e.currentTarget) setShowBooking(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="bg-apex-blue-700 rounded-t-2xl px-6 py-5">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-white/60 text-xs font-bold uppercase tracking-wider">Create Booking</p>
                  <p className="text-white font-black text-lg mt-0.5">{showBooking.vehicle}</p>
                  <p className="text-white/50 text-xs mt-0.5">{showBooking.customer_name}</p>
                </div>
                <button onClick={() => setShowBooking(null)} className="bg-white/15 text-white p-2 rounded-lg"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-600">
                A booking will be created for <strong>{showBooking.customer_name}</strong> — Cash for Cars — <strong>{showBooking.vehicle}</strong>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Date *</span>
                  <input type="date" value={bookingForm.scheduled_date} onChange={sbf('scheduled_date')} className={`${inputCls} mt-1`} />
                </label>
                <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Time</span>
                  <input type="time" value={bookingForm.scheduled_time} onChange={sbf('scheduled_time')} className={`${inputCls} mt-1`} />
                </label>
              </div>
              <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Final Charge ($)</span>
                <input type="number" value={bookingForm.charge} onChange={sbf('charge')} className={`${inputCls} mt-1`} placeholder={String(showBooking.paid||'')} />
              </label>
              <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Notes</span>
                <textarea value={bookingForm.notes} onChange={sbf('notes')} className={`${inputCls} mt-1 resize-none`} rows={2} placeholder="Any additional notes..." />
              </label>
              <button onClick={createBooking} disabled={!bookingForm.scheduled_date || saving}
                className="w-full py-3.5 bg-apex-yellow-500 text-apex-blue-700 font-black rounded-xl hover:bg-apex-yellow-400 disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <Loader className="w-4 h-4 animate-spin" /> : <><Calendar className="w-4 h-4" /> Create Booking</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Purchase Modal ── */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => { if(e.target===e.currentTarget) setShowAdd(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[92vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <h2 className="font-black text-apex-blue-700">Record Car Purchase</h2>
              <button onClick={() => setShowAdd(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="overflow-y-auto p-6 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Date *</span><input type="date" value={form.date} onChange={sf('date')} className={`${inputCls} mt-1`} /></label>
                <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Amount Paid ($) *</span><input type="number" value={form.paid} onChange={sf('paid')} className={`${inputCls} mt-1`} placeholder="500" /></label>
                <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Customer Name *</span><input value={form.customer_name} onChange={sf('customer_name')} className={`${inputCls} mt-1`} placeholder="John Smith" /></label>
                <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Phone</span><input value={form.customer_phone} onChange={sf('customer_phone')} className={`${inputCls} mt-1`} placeholder="07 0000 0000" /></label>
              </div>
              <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Vehicle (Year Make Model) *</span><input value={form.vehicle} onChange={sf('vehicle')} className={`${inputCls} mt-1`} placeholder="2015 Toyota Corolla" /></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Rego</span><input value={form.rego} onChange={sf('rego')} className={`${inputCls} mt-1`} placeholder="123 ABC" /></label>
                <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Condition *</span>
                  <select value={form.condition} onChange={sf('condition')} className={`${selectCls} mt-1`}>
                    <option value="">Select</option>
                    {['Excellent','Good','Fair','Poor','Scrap'].map(c => <option key={c}>{c}</option>)}
                  </select>
                </label>
              </div>
              <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Notes</span><textarea value={form.notes} onChange={sf('notes')} className={`${inputCls} mt-1 resize-none`} rows={2} /></label>

              {/* Photo upload */}
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Vehicle Photos</p>
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    {photos.map((src, i) => (
                      <div key={i} className="relative aspect-[4/3] rounded-xl overflow-hidden bg-gray-100">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setPhotos(p => p.filter((_,idx)=>idx!==i))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {uploading ? (
                  <div className="flex items-center gap-2 text-sm text-apex-blue-600 py-2"><Loader className="w-4 h-4 animate-spin" /> Uploading...</div>
                ) : (
                  <div className="flex gap-2">
                    <button type="button" onClick={() => cameraRef.current?.click()}
                      className="flex-1 py-2.5 border-2 border-dashed border-apex-blue-200 rounded-xl text-xs font-bold text-apex-blue-600 hover:bg-apex-blue-50 flex items-center justify-center gap-1.5">
                      <Camera className="w-4 h-4" /> Take Photo
                    </button>
                    <button type="button" onClick={() => galleryRef.current?.click()}
                      className="flex-1 py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-xs font-bold text-gray-500 hover:border-apex-blue-200 hover:text-apex-blue-500 flex items-center justify-center gap-1.5">
                      <ImageIcon className="w-4 h-4" /> Choose Files
                    </button>
                  </div>
                )}
              </div>

              <button onClick={add} disabled={!form.date||!form.customer_name||!form.vehicle||!form.condition||!form.paid||saving}
                className="w-full bg-apex-yellow-500 text-apex-blue-700 py-3 rounded-xl font-black hover:bg-apex-yellow-400 disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <Loader className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Record Purchase</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}